import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useGeotabClient } from "@/components/GeotabClientContext";
import { REASON_NA } from "@/constants/constants";
import { TRIP_ANNOTATIONS_QUERY_KEY } from "@/hooks/tripAnnotations/useTripAnnotationsForDevices";
import { UnexpectedError } from "@/utilities/error/UnexpectedError";
import removeAnnotationsForTrips from "@/utilities/logbook/annotation/removeAnnotationsForTrips";
import setAnnotationForTrips from "@/utilities/logbook/annotation/setAnnotationForTrips";
import { setDriverForTrip } from "@/utilities/trip/setDriverForTrip";
import { TripRef } from "@/utilities/trip/TripRef";

export interface TripChanges {
  /** The id of the driver to set the trips to, `UNKNOWN_DRIVER_ID` to unassign the driver. */
  userId?: string;
  /** The trip reason to assign to the trips, {@link REASON_NA} to remove it. */
  reason?: string;
}

export interface SaveChangesArgs extends TripChanges {
  /** The trips to change. */
  trips: TripRef[];
}

/**
 * Saves driver and reason for the given trips: the reason as the trip's annotation, the driver
 * as a TripDriver DriverChange at trip start. Shared by the Fahrtenbuch and the audit page.
 */
export function useSaveChanges() {
  const { geotabClient } = useGeotabClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ trips, userId, reason }: SaveChangesArgs) => {
      if (trips.length === 0) {
        throw new UnexpectedError("No trips selected");
      }

      if (!userId && !reason) {
        throw new UnexpectedError("No changes to save");
      }

      const actions: Promise<unknown>[] = [];

      if (reason === REASON_NA) {
        actions.push(removeAnnotationsForTrips(geotabClient, trips));
      } else if (reason) {
        actions.push(setAnnotationForTrips(geotabClient, reason, trips));
      }

      if (userId) {
        for (const trip of trips) {
          actions.push(setDriverForTrip(trip, userId, geotabClient));
        }
      }

      await Promise.all(actions);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [TRIP_ANNOTATIONS_QUERY_KEY] });
    },
  });
}
